import React, { useState, useEffect } from 'react'
import { C, Avatar, Track, Mono, Spinner, FormTrend } from './ui'
import { getPlayerProps, getPlayerGamelogs, getPlayerVsPitcher, todayStr } from '../api/client'

const PROP_LABELS = {
  'HRR_over_1.5': 'HRR', 'total_bases': 'TB',
  'HR_anytime': 'HR', 'strikeouts': 'K', 'doubles': '2B'
}
const PROP_ICONS = {
  'HRR_over_1.5': '📊', 'total_bases': '🔥', 'HR_anytime': '💣', 'strikeouts': '⚡', 'doubles': '2️⃣'
}
const PROP_LINES = {
  'HRR_over_1.5': 1.5, 'total_bases': 1.5, 'HR_anytime': 0.5, 'strikeouts': 4.5, 'doubles': 0.5
}
const CORRELATIONS = [
  ['HRR_over_1.5', 'total_bases', 'High — same batting outcomes'],
  ['HRR_over_1.5', 'doubles', 'Moderate — extra-base drives HRR'],
  ['total_bases', 'HR_anytime', 'HR is subset of TB — strong overlap'],
  ['HR_anytime', 'total_bases', 'Power combo — HR drives TB'],
]

// ── Bettor-friendly tiers — Whale now 0.70+ ──────────────────────────────────
function getTierColor(prob) {
  if (prob >= 0.70) return C.green
  if (prob >= 0.52) return C.blue
  if (prob >= 0.40) return C.yellow
  return C.dim
}
function getTierLabel(prob) {
  if (prob >= 0.70) return '🔥 Top Play'
  if (prob >= 0.52) return '✅ Solid'
  if (prob >= 0.40) return '→ Lean'
  return '⚪ Skip'
}
function fmtOdds(odds) {
  if (odds == null) return '—'
  const v = parseInt(odds)
  if (isNaN(v)) return String(odds)
  return v > 0 ? `+${v}` : String(v)
}
function probToAmerican(prob) {
  if (!prob || prob <= 0 || prob >= 1) return '—'
  if (prob >= 0.5) return String(-Math.round((prob / (1 - prob)) * 100))
  return `+${Math.round(((1 - prob) / prob) * 100)}`
}
function Sparkline({ values, color = C.blue, width = 60, height = 20 }) {
  if (!values || values.length < 2) return null
  const mn = Math.min(...values), mx = Math.max(...values)
  const rng = (mx - mn) || 1
  const pts = values.map((v, i) => {
    const x = i * width / (values.length - 1)
    const y = height - Math.max(2, ((v - mn) / rng) * (height - 4))
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  const fillPts = `0,${height} ${pts} ${width},${height}`
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polygon points={fillPts} fill={color} fillOpacity="0.15" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function FactorBar({ icon, label, value, desc }) {
  const isBoost = value >= 1.03
  const isHurt  = value <= 0.97
  const color   = isBoost ? C.green : isHurt ? C.red : C.dim
  const pct     = Math.min(100, Math.max(0, ((value - 0.7) / (1.4 - 0.7)) * 100))
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13 }}>{icon}</span>
          <span style={{ fontSize: 11, color: C.text, fontWeight: 600 }}>{label}</span>
          {desc && <span style={{ fontSize: 10, color: C.dim }}>{desc}</span>}
        </div>
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700, color }}>
          {value >= 1 ? '+' : ''}{((value - 1) * 100).toFixed(0)}%
        </span>
      </div>
      <div style={{ height: 5, background: C.dim2, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${color}88, ${color})`, borderRadius: 3, transition: 'width .6s ease' }} />
      </div>
    </div>
  )
}
function WaterfallRow({ label, value, arrow, color, isTotal }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 12px', background: isTotal ? `${C.green}10` : 'transparent', borderTop: isTotal ? `1px solid ${C.dim2}` : 'none' }}>
      <div style={{ fontSize: isTotal ? 13 : 11, fontWeight: isTotal ? 700 : 400, color: C.text }}>{arrow} {label}</div>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: isTotal ? 14 : 12, fontWeight: isTotal ? 800 : 600, color }}>{value}</div>
    </div>
  )
}

// ── Best Way to Bet Him — replaces Risk Profile ───────────────────────────────
function BestWayToBet({ sortedProps, bestType, bestLine, bestProb, mainFair }) {
  if (sortedProps.length === 0) return null

  // Best Bet = top prop by prob
  const best = sortedProps[0]
  const bestPt   = best[0], bestE = best[1]
  const bestP    = parseFloat(bestE.prob_over || 0)

  // Safe Alternate = lowest line / safest prop (prefer TB O1.5 or HRR O1.5 under 0.65)
  const safeEntry = sortedProps.find(([pt]) => ['total_bases','HRR_over_1.5'].includes(pt)) || sortedProps[sortedProps.length - 1]
  const safePt   = safeEntry[0], safeE = safeEntry[1]
  const safeP    = parseFloat(safeE.prob_over || 0)
  const safeLine = parseFloat(safeE.line || PROP_LINES[safePt] || 1.5)

  // High Upside = HR or highest-line prop
  const upsideEntry = sortedProps.find(([pt]) => pt === 'HR_anytime') || sortedProps[Math.min(1, sortedProps.length - 1)]
  const upPt     = upsideEntry[0], upE = upsideEntry[1]
  const upP      = parseFloat(upE.prob_over || 0)
  const upLine   = parseFloat(upE.line || PROP_LINES[upPt] || 0.5)

  const cards = [
    {
      role:  '⭐ BEST BET',
      pt:    bestPt,
      line:  parseFloat(bestE.line || PROP_LINES[bestPt] || 1.5),
      prob:  bestP,
      fair:  bestE.implied_odds_over || probToAmerican(bestP),
      color: C.green,
      note:  'Highest hit chance',
    },
    {
      role:  '🛡️ SAFE PLAY',
      pt:    safePt,
      line:  safeLine,
      prob:  safeP,
      fair:  safeE.implied_odds_over || probToAmerican(safeP),
      color: C.blue,
      note:  'Lower risk floor',
    },
    {
      role:  '🚀 HIGH UPSIDE',
      pt:    upPt,
      line:  upLine,
      prob:  upP,
      fair:  upE.implied_odds_over || probToAmerican(upP),
      color: '#A855F7',
      note:  'Big payout potential',
    },
  ]

  return (
    <>
      <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
        🎯 BEST WAY TO BET HIM
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {cards.map((c, i) => (
          <div key={i} style={{
            flex: 1, background: C.bk3,
            border: `1px solid ${c.color}44`, borderTop: `3px solid ${c.color}`,
            borderRadius: 10, padding: '10px 8px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>{c.role}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 2 }}>
              {PROP_ICONS[c.pt] || ''} {PROP_LABELS[c.pt] || c.pt} O{c.line.toFixed(1)}
            </div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color: c.color, lineHeight: 1 }}>
              {(c.prob * 100).toFixed(0)}%
            </div>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: C.dim, marginTop: 2 }}>{c.fair}</div>
            <div style={{ fontSize: 8, color: C.dim, marginTop: 3 }}>{c.note}</div>
          </div>
        ))}
      </div>
    </>
  )
}

// ── Game Outcome / Moneyline Table ────────────────────────────────────────────
function GameOutcomeTable({ player, pitcher, venue, team }) {
  // Derive implied team context from what we have
  // Real moneyline can be wired when odds feed is connected
  const parkF  = parseFloat(player.park_factor || 1.0)
  const pitchA = parseFloat(player.pitcher_adj || 1.0)

  // Rough implied win % heuristic from park + pitcher context
  // Neutral = 50%, tough pitcher = lower, hitter's park = slightly higher
  const impliedWin = Math.min(72, Math.max(28, Math.round(50 + (parkF - 1) * 30 - (pitchA - 1) * 20)))
  const impliedML  = probToAmerican(impliedWin / 100)

  const bookML     = player.book_ml || player.moneyline || null
  const gameTotal  = player.implied_total || null

  return (
    <div style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 9, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>
          🏟️ GAME ODDS
        </div>
        <div style={{ fontSize: 9, color: C.dim }}>{venue || 'Venue TBD'}</div>
      </div>
      {/* Matchup row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 4, padding: '10px 14px', borderBottom: `1px solid ${C.dim2}`, alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 16, color: C.text }}>{team || '—'}</div>
          <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Batter's Team</div>
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: C.dim, fontWeight: 700, padding: '0 8px' }}>VS</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 14, color: C.dim }}>{pitcher || 'TBD'}</div>
          <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Starting Pitcher</div>
        </div>
      </div>
      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
        {[
          { l: 'Team ML',     v: bookML ? fmtOdds(bookML) : impliedML, c: C.blue,  note: bookML ? 'book' : 'derived' },
          { l: 'Win Chance',  v: `${impliedWin}%`,                     c: impliedWin >= 55 ? C.green : C.dim, note: 'model est.' },
          { l: 'Game Total',  v: gameTotal ? gameTotal.toFixed(1) : '—', c: C.text, note: 'O/U' },
        ].map((s, i) => (
          <div key={i} style={{
            textAlign: 'center', padding: '10px 8px',
            borderRight: i < 2 ? `1px solid ${C.dim2}` : 'none',
          }}>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 18, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
            <div style={{ fontSize: 7, color: C.dim, marginTop: 1, opacity: 0.7 }}>{s.note}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function PlayerDrawer({ player, onClose, onSavePlay }) {
  const [props,     setProps]     = useState([])
  const [logs,      setLogs]      = useState([])
  const [vsp,       setVsp]       = useState({})
  const [loading,   setLoading]   = useState(true)
  const [activeTab, setActiveTab] = useState('why')
  const today = todayStr()

  const name    = player.full_name || ''
  const team    = player.team || ''
  const venue   = player.venue || ''
  const slot    = player.lineup_slot
  const pid     = player.player_id

  // Detect pitcher: negative ID or strikeouts prop
  const isPitcher = (pid != null && pid < 0) || player.prop_key === 'strikeouts' || player.prop_type === 'strikeouts'
  const pitcher   = isPitcher ? '' : (player.opp_pitcher_name || 'TBD')
  const hand      = isPitcher ? (player.opp_pitcher_hand || player.throws || '') : (player.opp_pitcher_hand || '')
  const bats      = (player.bats || '').toUpperCase()
  // Platoon advantage: opposite hands = favorable for hitter
  const platoonFavorable = !isPitcher && bats && hand && bats !== hand && bats !== 'S'
  const platoonTough     = !isPitcher && bats && hand && bats === hand && bats !== 'S'
  const matchupLabel     = !isPitcher && bats && hand ? `${bats} vs ${hand}HP` : (hand ? `${hand}HP` : 'TBD')
  const matchupColor     = platoonFavorable ? C.green : platoonTough ? C.red : C.dim
  const parkF   = parseFloat(player.park_factor || 1.0)
  const pitchA  = parseFloat(player.pitcher_adj || 1.0)
  const lam     = parseFloat(player.lambda_adjusted || 0)
  const mainProb = parseFloat(player.prob_over || 0)
  const mainLine = parseFloat(player.line || 1.5)
  const mainFair = player.implied_odds_over || probToAmerican(mainProb)
  const mainBook  = player.book_over
  const mainUnder = player.book_under
  const mainEdge  = player.edge_pct
  const bookUsed  = player.book_used
  const noVigWarn = player.warning_flags?.includes?.('no_vig_unavailable')

  useEffect(() => {
    setLoading(true)
    const load = async () => {
      try {
        const [propsData, logsData] = await Promise.all([
          pid ? getPlayerProps(pid, today)     : Promise.resolve(null),
          pid ? getPlayerGamelogs(pid)         : Promise.resolve(null),
        ])
        if (propsData?.props) setProps(propsData.props)
        if (logsData?.logs)   setLogs(logsData.logs)
        if (pitcher && pitcher !== 'TBD' && pid) {
          try {
            const vspData = await getPlayerVsPitcher(pid, pitcher)
            if (vspData) setVsp(vspData)
          } catch (e) {}
        }
      } catch (e) {}
      setLoading(false)
    }
    load()
  }, [pid, today])

  const propMap = {}
  props.forEach(p => { propMap[p.prop_type] = p })
  const mainPropType = isPitcher ? 'strikeouts' : (player.prop_key || 'HRR_over_1.5')
  if (!propMap[mainPropType] && mainProb > 0) {
    propMap[mainPropType] = { prop_type: mainPropType, line: mainLine, lambda_adjusted: lam, prob_over: mainProb, park_factor: parkF, pitcher_adj: pitchA, opp_pitcher_name: pitcher, venue, implied_odds_over: mainFair, book_over: mainBook, book_under: mainUnder, book_used: bookUsed, edge_pct: mainEdge, warning_flags: player.warning_flags }
  }
  // Pitchers: only show strikeouts prop
  const allSortedProps = Object.entries(propMap).sort(([, a], [, b]) => parseFloat(b.prob_over || 0) - parseFloat(a.prob_over || 0))
  const sortedProps = isPitcher ? allSortedProps.filter(([pt]) => pt === 'strikeouts') : allSortedProps
  const bestPropEntry = sortedProps[0]
  const bestProb = bestPropEntry ? parseFloat(bestPropEntry[1].prob_over || 0) : mainProb
  const bestType = bestPropEntry ? bestPropEntry[0] : mainPropType
  const bestLine = bestPropEntry ? parseFloat(bestPropEntry[1].line || 1.5) : mainLine

  const l5   = logs.slice(0, 5)
  const l10  = logs.slice(0, 10)
  const hrr  = g => (parseInt(g.H || 0) + parseInt(g.R || 0) + parseInt(g.RBI || 0))
  const l5Avg  = l5.length  > 0 ? l5.reduce((s, g)  => s + hrr(g), 0) / l5.length  : null
  const l10Avg = l10.length > 0 ? l10.reduce((s, g) => s + hrr(g), 0) / l10.length : null
  const isHot  = l5Avg != null && l5Avg >= 2.2
  const isCold = l5Avg != null && l5Avg <= 0.8

  const baseL       = lam / Math.max(parkF * pitchA, 0.01)
  const parkContrib = baseL * parkF - baseL
  const pitchContrib = baseL * pitchA - baseL
  const SLOT_MULT   = { 1:1.08, 2:1.07, 3:1.06, 4:1.05, 5:1.03, 6:1.00, 7:0.97, 8:0.95, 9:0.93 }
  const slotMult    = SLOT_MULT[slot] || 1.0
  const slotContrib = baseL * slotMult - baseL
  const residual    = lam - (baseL + parkContrib + pitchContrib + slotContrib)

  const factors = [
    { icon: '🔥', label: 'Recent Form',      value: l5Avg != null ? Math.min(1.35, 0.7 + (l5Avg / 2.5) * 0.65) : 1.0, desc: l5Avg != null ? `${l5Avg.toFixed(1)} HRR/g last 5` : 'No data' },
    { icon: '📋', label: 'Lineup Spot',      value: slotMult,   desc: slot ? `Batting ${slot}` : 'Pending' },
    { icon: '⚾', label: 'Pitcher Weakness', value: pitchA,     desc: pitcher.slice(0, 16) },
    { icon: '🏠', label: 'Park Boost',       value: parkF,      desc: venue.slice(0, 18) },
  ]

  // Matchup Fit (Statcast zone + contact + pitch-type layer)
  const fit = player.matchup_fit || props.find(p => p.matchup_fit)?.matchup_fit
  const fitNeutral = fit?.source?.startsWith?.('neutral_fallback')

  let combo = null
  for (const [a, b, note] of CORRELATIONS) {
    if (propMap[a] && propMap[b]) { combo = [a, b, note]; break }
  }
  const hasBvP = vsp && parseInt(vsp.ab || 0) > 0

  const tabs = [
    { id: 'why',     label: 'WHY'       },
    { id: 'props',   label: 'BETS'      },
    { id: 'ladder',  label: 'HOW TO BET'},
    { id: 'history', label: 'HISTORY'   },
  ]

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        className="slide-up"
        style={{ width: '100%', maxWidth: 430, background: `linear-gradient(180deg, #0C1020 0%, ${C.bk} 100%)`, borderRadius: '20px 20px 0 0', border: `1px solid ${C.border}`, maxHeight: '94vh', overflowY: 'auto', boxShadow: `0 -12px 60px ${C.blue}25` }}
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: C.dim2 }} />
        </div>

        {/* ── Hero Header ── */}
        <div style={{ padding: '14px 16px 16px', background: `radial-gradient(ellipse 90% 60% at 50% 0%, ${C.blue}22 0%, transparent 70%)`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <Avatar name={name} idx={0} size={56} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 24, lineHeight: 1, color: C.text }}>{name}</div>
                {!isPitcher && <FormTrend trend={player.form_trend} delta={player.form_delta_pct} size={13} />}
              </div>
              <div style={{ fontSize: 12, color: C.dim, marginTop: 3 }}>
                {isPitcher
                  ? `${team || 'SP'} · Starting Pitcher${hand ? ` · ${hand}HP` : ''}`
                  : `${team} · vs ${pitcher} ${hand ? `(${hand}HP)` : ''}`}
              </div>
              <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{venue}</div>
              {slot && <div style={{ fontSize: 10, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, marginTop: 2 }}>BATTING {slot}</div>}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 36, color: getTierColor(bestProb), lineHeight: 1 }}>{(bestProb * 100).toFixed(0)}%</div>
              <div style={{ fontSize: 9, color: getTierColor(bestProb), fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, marginTop: 2 }}>{getTierLabel(bestProb)}</div>
              {isHot  && <div style={{ fontSize: 10, color: C.red,    fontWeight: 700 }}>🔥 HOT</div>}
              {isCold && <div style={{ fontSize: 10, color: C.blueHi, fontWeight: 700 }}>🧊 COLD</div>}
            </div>
          </div>
          {/* Context pills */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              { l: 'PARK',    v: parkF  >= 1.05 ? `+${((parkF-1)*100).toFixed(0)}%`  : parkF  <= 0.95 ? `${((parkF-1)*100).toFixed(0)}%`  : 'Neutral', c: parkF  >= 1.05 ? C.green : parkF  <= 0.95 ? C.red : C.dim },
              { l: 'PITCHER', v: isPitcher ? (hand ? `${hand}HP` : 'SP') : (pitchA >= 1.05 ? 'Soft' : pitchA <= 0.95 ? 'Tough' : 'Average'), c: isPitcher ? C.blue : (pitchA >= 1.05 ? C.green : pitchA <= 0.95 ? C.red : C.dim) },
              { l: isPitcher ? 'ROLE' : 'LINEUP',  v: isPitcher ? 'Starter' : (slot ? `Slot ${slot}` : 'Pending'), c: C.dim },
              { l: isPitcher ? 'THROWS' : 'MATCHUP', v: isPitcher ? (hand ? `${hand}HP` : 'TBD') : matchupLabel, c: isPitcher ? C.dim : matchupColor },
            ].map((s, i) => (
              <div key={i} style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 7, padding: '4px 8px' }}>
                <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em' }}>{s.l}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: s.c, marginTop: 1 }}>{s.v}</div>
              </div>
            ))}
          </div>
          {/* Best market strip */}
          <div style={{ background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 9, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em' }}>⚡ BEST BET TODAY</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 3 }}>{PROP_ICONS[bestType]} {PROP_LABELS[bestType] || bestType} O{bestLine.toFixed(1)}</div>
              <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>
                Fair: {bestPropEntry?.[1]?.implied_odds_over || mainFair}
                {mainBook && <span style={{ color: C.blue, marginLeft: 8 }}>O: {fmtOdds(mainBook)}</span>}
                {mainUnder && <span style={{ color: C.dim, marginLeft: 4 }}>U: {fmtOdds(mainUnder)}</span>}
                {mainEdge && mainEdge > 0 && <span style={{ color: noVigWarn ? C.yellow : C.green, marginLeft: 8 }}>+{(mainEdge * 100).toFixed(1)}%{noVigWarn ? '~' : ''}</span>}
              </div>
              {bookUsed && <div style={{ fontSize: 9, color: C.dim, marginTop: 1 }}>via {bookUsed}</div>}
            </div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 30, color: C.blue }}>{(bestProb * 100).toFixed(0)}%</div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, zIndex: 10, background: '#0C1020' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              flex: 1, background: 'none', border: 'none', padding: '11px 0',
              fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 10,
              letterSpacing: '.06em', cursor: 'pointer',
              color: activeTab === t.id ? C.blue : C.dim,
              borderBottom: `2px solid ${activeTab === t.id ? C.blue : 'transparent'}`,
              transition: 'all .18s',
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── Tab Content ── */}
        <div style={{ padding: '14px 16px' }}>
          {loading && <Spinner />}

          {/* ── WHY WE LIKE HIM ── */}
          {!loading && activeTab === 'why' && (
            <div>
              <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 }}>
                ✅ WHY WE LIKE HIM
              </div>
              {factors.map((f, i) => <FactorBar key={i} {...f} />)}

              {/* Matchup Fit (zone + contact + pitch type) */}
              {fit && (<>
                <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', margin: '18px 0 8px' }}>
                  🎯 MATCHUP FIT {fitNeutral && <span style={{ color: C.yellow, marginLeft: 6 }}>· limited data</span>}
                </div>
                <FactorBar icon="🎯" label="Zone Fit"        value={1.0 + (parseFloat(fit.zone_fit_score)            - 0.5) * 0.6} desc={`${(parseFloat(fit.zone_fit_score)*100).toFixed(0)}%`} />
                <FactorBar icon="🎲" label="Pitch Type Fit"  value={1.0 + (parseFloat(fit.pitch_type_fit_score)      - 0.5) * 0.6} desc={`${(parseFloat(fit.pitch_type_fit_score)*100).toFixed(0)}%`} />
                <FactorBar icon="💎" label="Contact Quality" value={1.0 + (parseFloat(fit.contact_quality_fit_score) - 0.5) * 0.6} desc={`${(parseFloat(fit.contact_quality_fit_score)*100).toFixed(0)}%`} />
                <FactorBar icon="⚠️" label="Swing-Miss Risk" value={1.0 - parseFloat(fit.swing_miss_risk) * 1.5}                desc={`${(parseFloat(fit.swing_miss_risk)*100).toFixed(1)}% above avg`} />
                <FactorBar icon="🧮" label="Fit Adjustment"  value={parseFloat(fit.hrr_matchup_fit_adj)}                       desc="applied to λ" />
              </>)}

              {/* Projection breakdown */}
              {lam > 0 && (<>
                <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', margin: '18px 0 8px' }}>
                  📐 PROJECTION BREAKDOWN
                </div>
                <div style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 4 }}>
                  <WaterfallRow label="Base λ"                                                              value={baseL.toFixed(3)}                             arrow="□" color={C.text} />
                  <WaterfallRow label={`Park (${parkF.toFixed(3)}× ${venue.slice(0,16)})`}                 value={`${parkContrib>=0?'+':''}${parkContrib.toFixed(3)}`}  arrow={parkContrib>0.03?'▲':parkContrib<-0.03?'▼':'→'}  color={parkContrib>0.03?C.green:parkContrib<-0.03?C.red:C.dim} />
                  <WaterfallRow label={`Pitcher (${pitchA.toFixed(3)}× ${pitcher.slice(0,14)})`}           value={`${pitchContrib>=0?'+':''}${pitchContrib.toFixed(3)}`} arrow={pitchContrib>0.03?'▲':pitchContrib<-0.03?'▼':'→'} color={pitchContrib>0.03?C.green:pitchContrib<-0.03?C.red:C.dim} />
                  <WaterfallRow label={`Lineup (${slot?`Slot ${slot}`:'pending'} x${slotMult.toFixed(2)})`} value={`${slotContrib>=0?'+':''}${slotContrib.toFixed(3)}`}  arrow={slotContrib>0.01?'▲':slotContrib<-0.01?'▼':'→'}  color={slotContrib>0.01?C.green:slotContrib<-0.01?C.red:C.dim} />
                  <WaterfallRow label="Recency / bullpen / other"                                           value={`${residual>=0?'+':''}${residual.toFixed(3)}`}        arrow={residual>0.03?'▲':residual<-0.03?'▼':'→'}        color={residual>0.03?C.green:residual<-0.03?C.red:C.dim} />
                  <WaterfallRow label="Final λ"                                                             value={lam.toFixed(3)}                               arrow="●" color={getTierColor(mainProb)} isTotal />
                </div>
                <div style={{ fontSize: 9, color: C.dim, textAlign: 'right', marginBottom: 14 }}>
                  λ={lam.toFixed(3)} vs line {mainLine.toFixed(1)} — projects {lam > mainLine ? 'OVER' : 'UNDER'} by {Math.abs(lam - mainLine).toFixed(2)}
                </div>
              </>)}

              {/* Game Outcome / Moneyline Table — batters only */}
              {!isPitcher && (<>
                <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                  🏟️ GAME OUTCOME
                </div>
                <GameOutcomeTable player={player} pitcher={pitcher} venue={venue} team={team} />
              </>)}

              {/* VS Pitcher History — batters only */}
              {!isPitcher && (<>
                <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                  ⚾ HISTORY VS PITCHER
                </div>
                <div style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                  {hasBvP ? (
                    <div>
                      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                        {vsp.h || 0}-for-{vsp.ab}
                        {vsp.hr  > 0 && <span style={{ color: C.green, marginLeft: 10 }}>{vsp.hr} HR</span>}
                        {vsp.rbi > 0 && <span style={{ color: C.blue,  marginLeft: 10 }}>{vsp.rbi} RBI</span>}
                      </div>
                      <div style={{ fontSize: 11, color: C.dim }}>
                        vs {pitcher} · AVG {vsp.ab > 0 ? `.${String(Math.round((vsp.h || 0) / (vsp.ab || 1) * 1000)).padStart(3, '0')}` : '—'}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: C.dim }}>No prior matchup data vs {pitcher}</div>
                  )}
                </div>
              </>)}

              {/* Recent form */}
              {l5.length > 0 && (<>
                <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                  📅 RECENT FORM
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                  {[
                    { label: 'L5 HRR',  vals: l5.map(g  => hrr(g)), avg: l5Avg  },
                    { label: 'L10 HRR', vals: l10.map(g => hrr(g)), avg: l10Avg },
                  ].map((s, i) => (
                    <div key={i} style={{ flex: 1, background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                        <Sparkline values={[...s.vals].reverse()} color={s.avg >= 2.0 ? C.green : s.avg >= 1.3 ? C.blue : C.yellow} />
                      </div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 18, color: s.avg >= 2.0 ? C.green : s.avg >= 1.3 ? C.blue : C.yellow, textAlign: 'center', lineHeight: 1 }}>
                        {s.avg != null ? s.avg.toFixed(1) : '—'}
                      </div>
                      <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', textAlign: 'center', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                  <div style={{ flex: 1, background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 10px' }}>
                    <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4, textAlign: 'center' }}>TREND</div>
                    <div style={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {l5.map((g, i) => {
                        const total = hrr(g); const hit = total >= 2
                        return (
                          <div key={i} style={{ width: 22, height: 22, borderRadius: 5, background: hit ? `${C.green}25` : `${C.red}20`, border: `1px solid ${hit ? C.green + '44' : C.red + '33'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 700, color: hit ? C.green : C.red }}>
                            {total}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </>)}
            </div>
          )}

          {/* ── ALL BETS FOR THIS PLAYER ── */}
          {!loading && activeTab === 'props' && (
            <div>
              <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
                📊 ALL BETS FOR THIS PLAYER
              </div>
              {sortedProps.length === 0
                ? <div style={{ textAlign: 'center', padding: '24px 0', color: C.dim, fontSize: 12 }}>No prop data — run auto slate first</div>
                : (
                  <div style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 44px 52px 52px 52px 60px', gap: 4, padding: '6px 12px', borderBottom: `1px solid ${C.border}` }}>
                      {['Prop', 'Line', 'Hit %', 'Fair', 'Book', 'Edge'].map((h, i) => (
                        <div key={h} style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', textAlign: i === 0 ? 'left' : 'center' }}>{h}</div>
                      ))}
                    </div>
                    {sortedProps.map(([pt, e]) => {
                      const prob = parseFloat(e.prob_over || 0)
                      const line = parseFloat(e.line || PROP_LINES[pt] || 1.5)
                      const color = getTierColor(prob)
                      const fair = e.implied_odds_over || probToAmerican(prob)
                      const book = e.book_over
                      const under = e.book_under
                      const edge = e.edge_pct
                      const hasEdge = edge != null && edge > 0.005
                      const nvWarn = e.warning_flags?.includes?.('no_vig_unavailable')
                      const isBest = pt === bestType
                      return (
                        <div key={pt} style={{ display: 'grid', gridTemplateColumns: '1fr 44px 52px 52px 52px 60px', gap: 4, padding: '9px 12px', borderBottom: `1px solid ${C.dim2}`, background: isBest ? `${C.blue}08` : 'transparent' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ fontSize: 12 }}>{PROP_ICONS[pt] || '📊'}</span>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{PROP_LABELS[pt] || pt}</div>
                              {isBest && <div style={{ fontSize: 8, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>★ BEST</div>}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: C.dim }}>O{line.toFixed(1)}</div>
                          <div style={{ textAlign: 'center', fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 15, color }}>{(prob * 100).toFixed(0)}%</div>
                          <div style={{ textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: C.blue }}>{fair}</div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: C.text }}>{fmtOdds(book)}</div>
                            {under != null && <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: C.dim }}>{fmtOdds(under)}</div>}
                            {e.book_used && <div style={{ fontSize: 8, color: C.dim }}>{e.book_used.replace('williamhill_us','wh').replace('betonlineag','bol')}</div>}
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            {hasEdge
                              ? <span style={{ background: nvWarn ? `${C.yellow}18` : `${C.green}18`, border: `1px solid ${nvWarn ? C.yellow : C.green}30`, borderRadius: 5, padding: '2px 5px', fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: nvWarn ? C.yellow : C.green }}>+{(edge * 100).toFixed(1)}%{nvWarn ? '~' : ''}</span>
                              : <span style={{ fontSize: 9, color, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>{getTierLabel(prob)}</span>}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

              {/* Best Way to Bet Him */}
              <BestWayToBet
                sortedProps={sortedProps}
                bestType={bestType}
                bestLine={bestLine}
                bestProb={bestProb}
                mainFair={mainFair}
              />

              {/* Correlated combo */}
              {combo && (<>
                <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>🔗 BEST SAME-GAME COMBO</div>
                <div style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                        {PROP_ICONS[combo[0]]} {PROP_LABELS[combo[0]]} O{parseFloat(propMap[combo[0]]?.line || 1.5).toFixed(1)}
                        <span style={{ color: C.dim, margin: '0 8px' }}>+</span>
                        {PROP_ICONS[combo[1]]} {PROP_LABELS[combo[1]]} O{parseFloat(propMap[combo[1]]?.line || 1.5).toFixed(1)}
                      </div>
                      <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>{combo[2]}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color: '#A855F7' }}>
                        {((parseFloat(propMap[combo[0]]?.prob_over || 0) * parseFloat(propMap[combo[1]]?.prob_over || 0)) * 100).toFixed(0)}%
                      </div>
                      <div style={{ fontSize: 9, color: C.dim }}>combined hit chance</div>
                    </div>
                  </div>
                </div>
              </>)}
            </div>
          )}

          {/* ── HOW TO BET HIM ── */}
          {!loading && activeTab === 'ladder' && (
            <div>
              <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
                🪜 BET SIZING GUIDE
              </div>
              <div style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                {sortedProps.map(([pt, e], i) => {
                  const prob = parseFloat(e.prob_over || 0)
                  const line = parseFloat(e.line || PROP_LINES[pt] || 1.5)
                  const color = getTierColor(prob)
                  const roles = ['CORE PLAY', 'VALUE PLAY', 'SPRINKLE', 'OPTIONAL']
                  const tier  = prob >= 0.70 ? 'top' : prob >= 0.52 ? 'solid' : prob >= 0.40 ? 'lean' : 'skip'
                  const dots  = { top: '🟢', solid: '🔵', lean: '🟡', skip: '⚪' }
                  return (
                    <div key={pt} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: i < sortedProps.length - 1 ? `1px solid ${C.dim2}` : 'none' }}>
                      <span style={{ fontSize: 18 }}>{dots[tier]}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{PROP_ICONS[pt]} {PROP_LABELS[pt] || pt} O{line.toFixed(1)}</div>
                        <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 2 }}>{roles[i] || 'OPTIONAL'}</div>
                      </div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 20, color }}>{(prob * 100).toFixed(0)}%</div>
                    </div>
                  )
                })}
              </div>
              {sortedProps.length >= 2 && (<>
                <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                  🎰 PARLAY MATH
                </div>
                {[[0, 1], [0, 2], [0, 1, 2]].filter(c => c.every(i => sortedProps[i])).map((c, ci) => {
                  const combined = c.reduce((p, i) => p * parseFloat(sortedProps[i]?.[1]?.prob_over || 0), 1)
                  const fair = probToAmerican(combined)
                  return (
                    <div key={ci} style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{c.map(i => PROP_LABELS[sortedProps[i]?.[0]] || '').join(' + ')} parlay</div>
                        <div style={{ fontSize: 9, color: C.dim, marginTop: 2 }}>Fair odds: <span style={{ color: C.blue }}>{fair}</span></div>
                      </div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 20, color: C.blue }}>{(combined * 100).toFixed(0)}%</div>
                    </div>
                  )
                })}
              </>)}
            </div>
          )}

          {/* ── HISTORY ── */}
          {!loading && activeTab === 'history' && (
            <div>
              <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
                📅 GAME LOG
              </div>
              {logs.length === 0
                ? <div style={{ textAlign: 'center', padding: '24px 0', color: C.dim, fontSize: 12 }}>No game logs available</div>
                : (
                  <div style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 30px 30px 30px 36px 30px', gap: 4, padding: '6px 12px', borderBottom: `1px solid ${C.border}` }}>
                      {['Date', 'H', 'R', 'RBI', 'HRR', 'HR'].map((h, i) => (
                        <div key={h} style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', textAlign: i === 0 ? 'left' : 'center' }}>{h}</div>
                      ))}
                    </div>
                    {logs.slice(0, 15).map((g, i) => {
                      const h = parseInt(g.H || 0), r = parseInt(g.R || 0), rbi = parseInt(g.RBI || 0), hr2 = parseInt(g.HR || 0)
                      const total = h + r + rbi
                      const hit = total >= 2
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 30px 30px 30px 36px 30px', gap: 4, padding: '7px 12px', borderBottom: `1px solid ${C.dim2}`, background: hit ? `${C.green}08` : 'transparent' }}>
                          <div style={{ fontSize: 11, color: C.dim }}>{g.game_date?.slice(5) || '—'}</div>
                          {[h, r, rbi].map((v, j) => (
                            <div key={j} style={{ textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: C.text }}>{v}</div>
                          ))}
                          <div style={{ textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700, color: hit ? C.green : C.text }}>{total}</div>
                          <div style={{ textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: hr2 > 0 ? C.green : C.dim }}>{hr2}</div>
                        </div>
                      )
                    })}
                  </div>
                )}
            </div>
          )}
        </div>

        {/* ── Save Button ── */}
        <div style={{ padding: '0 16px 36px' }}>
          {onSavePlay && (
            <button
              onClick={() => {
                onSavePlay({
                  id: Date.now(), playerName: name, team,
                  market: PROP_LABELS[bestType] || bestType,
                  line: bestLine, odds: parseInt(mainFair) || -110,
                  confidence: Math.round(bestProb * 100),
                  edge: mainEdge ? (mainEdge * 100).toFixed(1) : null,
                  status: 'upcoming', createdAt: new Date().toISOString(),
                  idx: Math.floor(Math.random() * 6),
                })
                onClose()
              }}
              style={{ width: '100%', padding: '14px', background: `linear-gradient(135deg, ${C.blueDim}, ${C.blue})`, border: 'none', borderRadius: 12, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: '.06em', color: '#fff', cursor: 'pointer', boxShadow: `0 4px 24px ${C.blue}50` }}
            >
              + SAVE BEST PLAY
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
